import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Shield, Mail, Lock, Loader2, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { authApi } from '../api/auth';
import toast from 'react-hot-toast';

interface ResetRequestForm {
  email: string;
}

interface ResetConfirmForm {
  password: string;
  confirmPassword: string;
}

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const requestForm = useForm<ResetRequestForm>();
  const confirmForm = useForm<ResetConfirmForm>();

  const onRequestReset = async (data: ResetRequestForm) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      const response = await authApi.requestPasswordReset(data.email);
      
      if (response.success) {
        setIsEmailSent(true);
        toast.success('Password reset email sent!');
      } else {
        toast.error(response.error?.message || 'Failed to send reset email');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to send reset email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onConfirmReset = async (data: ResetConfirmForm) => {
    if (isSubmitting || !token) return;

    try {
      setIsSubmitting(true);
      const response = await authApi.confirmPasswordReset({
        token,
        password: data.password,
        confirmPassword: data.confirmPassword,
      });
      
      if (response.success) {
        toast.success('Password reset successfully! You can now log in.');
        // Redirect to login after a brief delay
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        toast.error(response.error?.message || 'Failed to reset password');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to reset password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-brand rounded-2xl flex items-center justify-center shadow-brand">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="mt-6 text-3xl font-bold text-gradient">VaultDrop</h1>
          <p className="mt-2 text-sm text-gray-600">
            Secure file upload system by <span className="font-medium text-brand-600">KEYper Systems</span>
          </p>
        </div>

        <div className="bg-white py-8 px-6 shadow-lg rounded-xl border border-gray-100">
          {!token ? (
            // Request Reset Form
            <>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Reset Password</h2>
                <p className="mt-2 text-sm text-gray-600">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              {!isEmailSent ? (
                <form className="space-y-6" onSubmit={requestForm.handleSubmit(onRequestReset)}>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        {...requestForm.register('email', {
                          required: 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address',
                          },
                          onChange: (e) => {
                            e.target.value = e.target.value.toLowerCase();
                          }
                        })}
                        type="email"
                        className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent sm:text-sm"
                        placeholder="Enter your email"
                        autoComplete="email"
                      />
                    </div>
                    {requestForm.formState.errors.email && (
                      <p className="mt-1 text-sm text-red-600">
                        {requestForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-brand hover:bg-gradient-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </button>
                </form>
              ) : (
                <div className="text-center space-y-4">
                  <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">Email Sent!</h3>
                  <p className="text-sm text-gray-600">
                    If an account with that email exists, you'll receive a password reset link shortly.
                  </p>
                  <p className="text-xs text-gray-500">
                    Check your spam folder if you don't see the email within a few minutes.
                  </p>
                </div>
              )}
            </>
          ) : (
            // Confirm Reset Form
            <>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Set New Password</h2>
                <p className="mt-2 text-sm text-gray-600">
                  Please enter your new password below.
                </p>
              </div>

              <form className="space-y-6" onSubmit={confirmForm.handleSubmit(onConfirmReset)}>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...confirmForm.register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 12,
                          message: 'Password must be at least 12 characters',
                        },
                        pattern: {
                          value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                          message: 'Password must contain uppercase, lowercase, number, and special character',
                        },
                      })}
                      type={showPassword ? 'text' : 'password'}
                      className="appearance-none block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent sm:text-sm"
                      placeholder="Enter new password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  {confirmForm.formState.errors.password && (
                    <p className="mt-1 text-sm text-red-600">
                      {confirmForm.formState.errors.password.message}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...confirmForm.register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: (value) => {
                          const password = confirmForm.watch('password');
                          return value === password || 'Passwords do not match';
                        },
                      })}
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="appearance-none block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent sm:text-sm"
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  {confirmForm.formState.errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">
                      {confirmForm.formState.errors.confirmPassword.message}
                    </p>
                  )}
                </div>

                {/* Password Requirements */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-blue-400 mt-0.5" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">Password Requirements:</h3>
                      <ul className="mt-2 text-sm text-blue-700 list-disc list-inside">
                        <li>At least 12 characters long</li>
                        <li>At least one uppercase letter</li>
                        <li>At least one lowercase letter</li>
                        <li>At least one number</li>
                        <li>At least one special character</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-brand hover:bg-gradient-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>
            </>
          )}

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-brand-600 hover:text-brand-500 font-medium"
            >
              ← Back to Login
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            Securely Delivered by KEYper Systems
          </p>
        </div>
      </div>
    </div>
  );
};